import React, { useState, useRef, useCallback } from 'react';
import StarredEventPreview from './StarredEventPreview';
import { FaStar, FaTimes, FaTrashAlt, FaGripVertical } from 'react-icons/fa';
import { FiMinimize2, FiMaximize2, FiGrid, FiZoomIn, FiZoomOut } from 'react-icons/fi';

const CANVAS_W = 4000;
const CANVAS_H = 3000;
const MIN_CARD_W = 300;
const MIN_CARD_H = 120;
const DEFAULT_CARD_W = 420;
const DEFAULT_CARD_H = 400;

/* ── Canvas card ── */
function CanvasCard({ item, onRemove, onMoveStart, onResizeStart, onBringToFront, onToggleMinimize }) {
  const contentHeight = item.minimized ? 0 : item.height;

  return (
    <div
      style={{
        position: 'absolute',
        left: item.x,
        top: item.y,
        width: item.width,
        zIndex: item.zIndex,
      }}
    >
      <div
        className="rounded-xl overflow-hidden shadow-2xl"
        style={{
          background: '#111827',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          height: item.minimized ? 'auto' : item.height + 36, // 36 = header height approx
        }}
        onMouseDown={() => onBringToFront(item.id)}
      >
        {/* Card header — drag handle */}
        <div
          className="flex flex-shrink-0 items-center gap-2 px-3 py-2 select-none"
          style={{
            background: 'linear-gradient(90deg,#1a2333,#1e2a3d)',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            cursor: 'grab',
          }}
          onMouseDown={(e) => {
            if (e.target.closest('button')) return;
            onMoveStart(e, item.id);
          }}
        >
          <FaGripVertical style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', flexShrink: 0 }} />
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <FaStar style={{ fontSize: 9, color: '#fbbf24', flexShrink: 0 }} />
            <span
              className="truncate text-[11.5px] font-semibold text-slate-100"
              title={item.event.FNAME || item.event.TDESC}
            >
              {item.event.FNAME || item.event.TDESC || '—'}
            </span>
          </div>
          {item.event.TICID && (
            <span
              className="flex-shrink-0 rounded px-1.5 py-0.5 font-mono text-[9px] text-slate-500"
              style={{ background: 'rgba(255,255,255,0.06)' }}
            >
              #{item.event.TICID}
            </span>
          )}
          <div className="flex flex-shrink-0 items-center gap-0.5">
            <button
              onClick={() => onToggleMinimize(item.id)}
              className="flex h-5 w-5 items-center justify-center rounded text-slate-500 transition hover:bg-white/10 hover:text-slate-300"
              title={item.minimized ? 'Genişlet' : 'Küçült'}
            >
              {item.minimized ? <FiMaximize2 size={10} /> : <FiMinimize2 size={10} />}
            </button>
            <button
              onClick={() => onRemove(item.id)}
              className="flex h-5 w-5 items-center justify-center rounded text-slate-500 transition hover:bg-red-500/20 hover:text-red-400"
              title="Kaldır"
            >
              <FaTimes size={10} />
            </button>
          </div>
        </div>

        {/* Content */}
        {!item.minimized && (
          <div
            className="flex-1 overflow-auto p-3"
            style={{
              height: contentHeight,
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.1) transparent',
            }}
          >
            {item.event.TDESC && item.event.TDESC !== item.event.FNAME && (
              <div className="mb-2 text-[11px] text-slate-400">{item.event.TDESC}</div>
            )}
            {item.event.FVALUE ? (
              <StarredEventPreview FVALUE={item.event.FVALUE} FNAME={item.event.FNAME} />
            ) : (
              <div className="py-4 text-center text-[11px] text-slate-600">İçerik yok</div>
            )}
          </div>
        )}
      </div>

      {/* ── Resize handles ── */}
      {!item.minimized && (
        <>
          {/* Right edge (E) */}
          <div
            style={{ position: 'absolute', right: -4, top: 8, bottom: 8, width: 8, cursor: 'ew-resize', zIndex: 2 }}
            onMouseDown={(e) => { e.stopPropagation(); onResizeStart(e, item.id, 'e'); }}
          />
          {/* Bottom edge (S) */}
          <div
            style={{ position: 'absolute', bottom: -4, left: 8, right: 8, height: 8, cursor: 'ns-resize', zIndex: 2 }}
            onMouseDown={(e) => { e.stopPropagation(); onResizeStart(e, item.id, 's'); }}
          />
          {/* Corner (SE) */}
          <div
            style={{
              position: 'absolute', right: -4, bottom: -4, width: 14, height: 14,
              cursor: 'nwse-resize', zIndex: 3,
              background: 'rgba(255,255,255,0.12)', borderRadius: '0 0 10px 0',
            }}
            onMouseDown={(e) => { e.stopPropagation(); onResizeStart(e, item.id, 'se'); }}
          />
        </>
      )}
    </div>
  );
}

/* ── Main Workspace ── */
const StarredWorkspace = ({ starredEvents, unstar, clearAll, onClose }) => {
  const [canvasItems, setCanvasItems] = useState([]);
  const [zoom, setZoom] = useState(1);
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const movingRef = useRef(null);
  const resizingRef = useRef(null);
  const dragEventRef = useRef(null);
  const zIndexCounter = useRef(100);

  /* ── Sidebar drag start ── */
  const handleSidebarDragStart = useCallback((e, event) => {
    dragEventRef.current = event;
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', String(event.EVEID));
  }, []);

  /* ── Canvas drop ── */
  const handleCanvasDrop = useCallback((e) => {
    e.preventDefault();
    const event = dragEventRef.current;
    if (!event) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, (e.clientX - rect.left) / zoom - DEFAULT_CARD_W / 2);
    const y = Math.max(0, (e.clientY - rect.top) / zoom - 20);

    const alreadyExists = canvasItems.some(
      (item) => item && item.event && String(item.event.EVEID) === String(event.EVEID),
    );

    if (!alreadyExists) {
      zIndexCounter.current += 1;
      const newItem = {
        id: `c-${Date.now()}`,
        event,
        x,
        y,
        width: DEFAULT_CARD_W,
        height: DEFAULT_CARD_H,
        minimized: false,
        zIndex: zIndexCounter.current,
      };
      setCanvasItems((prev) => [...prev.filter(Boolean), newItem]);
    }
    dragEventRef.current = null;
  }, [canvasItems, zoom]);

  /* ── Card bring to front ── */
  const handleBringToFront = useCallback((id) => {
    zIndexCounter.current += 1;
    const z = zIndexCounter.current;
    setCanvasItems((prev) => prev.filter(Boolean).map((i) => (i.id === id ? { ...i, zIndex: z } : i)));
  }, []);

  /* ── Card move ── */
  const handleMoveStart = useCallback((e, id) => {
    e.preventDefault();
    const item = canvasItems.find((i) => i.id === id);
    if (!item) return;

    handleBringToFront(id);

    movingRef.current = {
      id,
      startMX: e.clientX,
      startMY: e.clientY,
      startIX: item.x,
      startIY: item.y,
    };

    const onMouseMove = (e) => {
      const moving = movingRef.current;
      if (!moving) return;
      // Değerleri yerel değişkene al — state updater closure'ı ref'e bağımlı olmasın
      const movingId = moving.id;
      const dx = (e.clientX - moving.startMX) / zoom;
      const dy = (e.clientY - moving.startMY) / zoom;
      const newX = Math.max(0, moving.startIX + dx);
      const newY = Math.max(0, moving.startIY + dy);
      setCanvasItems((prev) =>
        prev.filter(Boolean).map((i) =>
          i.id === movingId ? { ...i, x: newX, y: newY } : i,
        ),
      );
    };

    const onMouseUp = () => {
      movingRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [canvasItems, zoom, handleBringToFront]);

  /* ── Card resize — direction: 'e' | 's' | 'se' ── */
  const handleResizeStart = useCallback((e, id, direction = 'e') => {
    e.preventDefault();
    const item = canvasItems.find((i) => i && i.id === id);
    if (!item) return;

    resizingRef.current = {
      id,
      direction,
      startMX: e.clientX,
      startMY: e.clientY,
      startW: item.width,
      startH: item.height,
    };

    const onMouseMove = (e) => {
      const r = resizingRef.current;
      if (!r) return;
      const rid = r.id;
      const dx = (e.clientX - r.startMX) / zoom;
      const dy = (e.clientY - r.startMY) / zoom;
      const newW = Math.max(MIN_CARD_W, r.startW + dx);
      const newH = Math.max(MIN_CARD_H, r.startH + dy);

      setCanvasItems((prev) =>
        prev.filter(Boolean).map((i) => {
          if (i.id !== rid) return i;
          if (r.direction === 'e') return { ...i, width: newW };
          if (r.direction === 's') return { ...i, height: newH };
          return { ...i, width: newW, height: newH };
        }),
      );
    };

    const onMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [canvasItems, zoom]);

  /* ── Toggle minimize ── */
  const handleToggleMinimize = useCallback((id) => {
    setCanvasItems((prev) =>
      prev.filter(Boolean).map((i) => (i.id === id ? { ...i, minimized: !i.minimized } : i)),
    );
  }, []);

  /* ── Remove card ── */
  const handleRemove = useCallback((id) => {
    setCanvasItems((prev) => prev.filter((i) => i && i.id !== id));
  }, []);

  /* ── Clear canvas ── */
  const handleClearCanvas = () => setCanvasItems([]);

  /* ── Auto-arrange (basic grid) ── */
  const handleArrange = () => {
    const cols = Math.max(1, Math.floor((CANVAS_W * 0.7) / (DEFAULT_CARD_W + 32)));
    setCanvasItems((prev) =>
      prev.filter(Boolean).map((item, idx) => ({
        ...item,
        x: 32 + (idx % cols) * (DEFAULT_CARD_W + 32),
        y: 32 + Math.floor(idx / cols) * 360,
      })),
    );
  };

  const onSidebarItemDoubleClick = useCallback((event) => {
    const alreadyExists = canvasItems.some(
      (item) => item && item.event && String(item.event.EVEID) === String(event.EVEID),
    );
    if (!alreadyExists) {
      zIndexCounter.current += 1;
      const validItems = canvasItems.filter(Boolean);
      const col = validItems.length % 3;
      const row = Math.floor(validItems.length / 3);
      const newItem = {
        id: `c-${Date.now()}`,
        event,
        x: 32 + col * (DEFAULT_CARD_W + 32),
        y: 32 + row * (DEFAULT_CARD_H + 48),
        width: DEFAULT_CARD_W,
        height: DEFAULT_CARD_H,
        minimized: false,
        zIndex: zIndexCounter.current,
      };
      setCanvasItems((prev) => [...prev.filter(Boolean), newItem]);
    }
  }, [canvasItems]);

  return (
    <div
      className="fixed inset-0 flex"
      style={{ zIndex: 99990, background: '#080d16' }}
    >
      {/* ── Left Sidebar ── */}
      <div
        className="flex w-56 flex-shrink-0 flex-col"
        style={{
          background: '#0d1526',
          borderRight: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        {/* Sidebar header */}
        <div
          className="flex items-center gap-2.5 border-b px-4 py-3"
          style={{ borderColor: 'rgba(255,255,255,0.07)' }}
        >
          <div
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg"
            style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.25)' }}
          >
            <FaStar style={{ fontSize: 11, color: '#fbbf24' }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[12px] font-semibold text-slate-100">Starred Events</div>
            <div className="text-[9.5px] text-slate-600">{starredEvents.length} event</div>
          </div>
        </div>

        {/* Hint */}
        <div
          className="border-b px-3 py-2 text-[10px] text-slate-600"
          style={{ borderColor: 'rgba(255,255,255,0.05)' }}
        >
          Sürükle veya çift tıkla → canvas
        </div>

        {/* List */}
        <div
          className="flex-1 overflow-y-auto"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.08) transparent' }}
        >
          {starredEvents.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 px-4">
              <FaStar style={{ fontSize: 20, color: 'rgba(251,191,36,0.2)' }} />
              <div className="text-center text-[11px] text-slate-600">Yıldızlı event yok</div>
            </div>
          ) : (
            starredEvents.map((event) => {
              const onCanvas = canvasItems.some(
                (item) => item && item.event && String(item.event.EVEID) === String(event.EVEID),
              );
              return (
                <div
                  key={String(event.EVEID)}
                  draggable
                  onDragStart={(e) => handleSidebarDragStart(e, event)}
                  onDoubleClick={() => onSidebarItemDoubleClick(event)}
                  className="group flex cursor-grab items-center gap-2 px-3 py-2 transition-colors active:cursor-grabbing"
                  style={{
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    background: onCanvas ? 'rgba(251,191,36,0.06)' : 'transparent',
                    opacity: onCanvas ? 0.7 : 1,
                  }}
                  onMouseEnter={(e) => { if (!onCanvas) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = onCanvas ? 'rgba(251,191,36,0.06)' : 'transparent'; }}
                >
                  <FaGripVertical style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[11px] font-medium text-slate-200">
                      {event.FNAME || event.TDESC || '—'}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      {event.EVEID && (
                        <span
                          className="rounded px-1 py-0.5 font-mono text-[9px] text-slate-500"
                          style={{ background: 'rgba(255,255,255,0.05)' }}
                        >
                          #{event.EVEID}
                        </span>
                      )}
                      {(event.ANDAT || event.starredAt) && (
                        <span className="text-[9px] text-slate-600">
                          {event.ANDAT
                            ? new Date(event.ANDAT).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: '2-digit' })
                            : new Date(event.starredAt).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </div>
                  {onCanvas && (
                    <div
                      className="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[8px] font-semibold"
                      style={{ background: 'rgba(251,191,36,0.2)', color: '#fbbf24' }}
                    >
                      ✓
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Sidebar footer actions */}
        <div
          className="flex flex-col gap-1.5 border-t p-3"
          style={{ borderColor: 'rgba(255,255,255,0.07)' }}
        >
          <button
            onClick={handleArrange}
            className="flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-[10.5px] font-medium text-slate-300 transition hover:bg-white/5"
            style={{ borderColor: 'rgba(255,255,255,0.1)' }}
          >
            <FiGrid size={11} />
            Otomatik Hizala
          </button>
          <button
            onClick={handleClearCanvas}
            disabled={canvasItems.length === 0}
            className="flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-[10.5px] font-medium text-red-400/70 transition hover:bg-red-500/10 disabled:opacity-30"
            style={{ borderColor: 'rgba(248,113,113,0.15)' }}
          >
            <FiGrid size={11} />
            Canvas'ı Temizle
          </button>
        </div>
      </div>

      {/* ── Canvas Area ── */}
      <div className="relative flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <div
          className="flex flex-shrink-0 items-center gap-3 border-b px-4 py-2"
          style={{
            background: '#0d1526',
            borderColor: 'rgba(255,255,255,0.07)',
          }}
        >
          <div className="flex-1 text-[11px] text-slate-500">
            {canvasItems.length === 0
              ? 'Soldaki listeden event\'leri sürükleyin veya çift tıklayın'
              : `${canvasItems.length} kart yerleştirildi`}
          </div>

          {/* Zoom */}
          <div
            className="flex items-center gap-1 rounded-lg border px-2 py-1"
            style={{ borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' }}
          >
            <button
              onClick={() => setZoom((z) => Math.max(0.3, +(z - 0.1).toFixed(1)))}
              className="text-slate-400 transition hover:text-slate-200"
            >
              <FiZoomOut size={13} />
            </button>
            <span className="w-10 text-center text-[10.5px] font-mono text-slate-300">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(1)))}
              className="text-slate-400 transition hover:text-slate-200"
            >
              <FiZoomIn size={13} />
            </button>
            <button
              onClick={() => setZoom(1)}
              className="ml-1 rounded px-1 text-[9px] text-slate-500 transition hover:bg-white/10 hover:text-slate-300"
            >
              Reset
            </button>
          </div>

          {/* Close */}
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition hover:bg-white/8 hover:text-slate-200"
            title="Kapat"
          >
            <FaTimes size={13} />
          </button>
        </div>

        {/* Scrollable canvas wrapper */}
        <div
          ref={wrapperRef}
          className="flex-1 overflow-auto"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.08) transparent' }}
        >
          <div
            ref={canvasRef}
            style={{
              position: 'relative',
              width: CANVAS_W * zoom,
              height: CANVAS_H * zoom,
              transformOrigin: '0 0',
              backgroundImage:
                'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)',
              backgroundSize: `${24 * zoom}px ${24 * zoom}px`,
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleCanvasDrop}
          >
            {/* Canvas items — rendered inside a scaled inner div */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: CANVAS_W,
                height: CANVAS_H,
                transform: `scale(${zoom})`,
                transformOrigin: '0 0',
              }}
            >
              {canvasItems.map((item) => (
                <CanvasCard
                  key={item.id}
                  item={item}
                  onRemove={handleRemove}
                  onMoveStart={handleMoveStart}
                  onResizeStart={handleResizeStart}
                  onBringToFront={handleBringToFront}
                  onToggleMinimize={handleToggleMinimize}
                />
              ))}
            </div>

            {/* Empty state hint */}
            {canvasItems.length === 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: `translate(-50%, -50%) scale(${1 / zoom})`,
                  textAlign: 'center',
                  pointerEvents: 'none',
                }}
              >
                <div
                  className="flex flex-col items-center gap-4"
                  style={{ color: 'rgba(255,255,255,0.08)' }}
                >
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                  </svg>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>Analiz Workspace</div>
                  <div style={{ fontSize: 12 }}>Soldaki event'leri buraya sürükleyin</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StarredWorkspace;
